import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import css from './SaaSCoachTestimonialsPage.css';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';
import SettingsLayoutWrapper from '../../custom-components/wrappers/settings-layout-wrapper/SettingsLayoutWrapper';
import { IconSpinner } from '../../components';
import {
  fetchMyTestimonials,
  submitTestimonial,
  deleteTestimonial,
} from '../../custom-ducks/testimonials.duck';
import {
  fetchMyCredentials,
  submitCredential,
  deleteCredential,
} from '../../custom-ducks/credentials.duck';
import { storage } from '../../util/firebase';
import certConfig from '../../config/coachCertifications.json';
import {
  MIN_VERIFIED_TESTIMONIALS,
  MIN_VERIFIED_CERTIFICATIONS,
  getVerificationStatus,
  meetsTierGate,
  TIERS,
} from '../../util/coachTier';
import UpgradeGate from '../../custom-components/elements/upgrade-gate/UpgradeGate';

// Use imported MIN_VERIFIED_TESTIMONIALS / MIN_VERIFIED_CERTIFICATIONS instead
const MAX_CRED_FILE_SIZE = 10 * 1024 * 1024; // 10 MB cap for credential uploads
const CERTS = (certConfig && certConfig.certifications) || [];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const STATUS_LABEL = {
  pending: { text: 'Awaiting confirmation', tone: 'pending' },
  confirmed: { text: 'Verified', tone: 'confirmed' },
  rejected: { text: 'Declined by reviewer', tone: 'rejected' },
  expired: { text: 'Link expired', tone: 'expired' },
  revoked: { text: 'Revoked', tone: 'revoked' },
};

const errorMessage = (err) => {
  const code = err?.response?.data?.error;
  switch (code) {
    case 'invalid-referee-name':
      return 'Please enter the referee\'s full name (2-100 characters).';
    case 'invalid-referee-email':
      return 'Please enter a valid email address.';
    case 'invalid-body-length':
      return 'The testimonial must be between 30 and 2000 characters.';
    case 'duplicate-referee':
      return 'You already have a pending or confirmed testimonial from this email address.';
    case 'rate-limited':
      return 'You can submit up to 5 testimonials per day. Please try again tomorrow.';
    case 'self-testimonial-blocked':
      return "That's your own email — testimonials need to come from a different person.";
    default:
      return 'Something went wrong. Please try again or contact support.';
  }
};

function SaaSCoachTestimonialsPage({
  testimonials,
  testimonialsFetching,
  credentials,
  credentialsFetching,
  currentUser,
  onFetch,
  onSubmit,
  onDelete,
  onFetchCreds,
  onSubmitCred,
  onDeleteCred,
}) {
  const [form, setForm] = useState({
    refereeName: '',
    refereeEmail: '',
    refereeOrg: '',
    refereeTitle: '',
    refereeLinkedInUrl: '',
    body: '',
    programOrEngagement: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    onFetch().catch(() => {});
    onFetchCreds().catch(() => {});
  }, []);

  // ── Credentials state ──
  const [credForm, setCredForm] = useState({
    certificationId: '',
    issuingOrgOther: '',
    titleOther: '',
    issuedYear: '',
    file: null,
    fileError: null,
    uploadProgress: 0,
    uploading: false,
    fileUrl: '',
    fileName: '',
    fileSize: 0,
    fileType: '',
    attested: false,
  });
  const [credSubmitting, setCredSubmitting] = useState(false);
  const [credSubmitError, setCredSubmitError] = useState(null);
  const [credSuccess, setCredSuccess] = useState(null);
  const [deletingCredId, setDeletingCredId] = useState(null);

  const selectedCert = CERTS.find((c) => c.id === credForm.certificationId);
  const isOther = selectedCert && selectedCert.id === 'other';

  const handleFileSelect = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > MAX_CRED_FILE_SIZE) {
      setCredForm({ ...credForm, fileError: 'File must be 10 MB or smaller.' });
      return;
    }
    const okType = /^(application\/pdf|image\/(jpeg|png|jpg))$/.test(f.type);
    if (!okType) {
      setCredForm({ ...credForm, fileError: 'Please upload a PDF, JPG, or PNG.' });
      return;
    }
    if (!storage) {
      setCredForm({ ...credForm, fileError: 'Upload is not configured for this environment.' });
      return;
    }
    setCredForm((prev) => ({ ...prev, file: f, fileError: null, uploading: true, uploadProgress: 0 }));
    const safe = f.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `coach-credentials/${Date.now()}_${safe}`;
    const task = storage.ref(path).put(f, { contentType: f.type, customMetadata: { originalName: f.name } });
    task.on(
      'state_changed',
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setCredForm((prev) => ({ ...prev, uploadProgress: pct }));
      },
      (err) => {
        setCredForm((prev) => ({ ...prev, uploading: false, fileError: 'Upload failed. Please try again.' }));
      },
      () => {
        task.snapshot.ref.getDownloadURL().then((url) => {
          setCredForm((prev) => ({
            ...prev,
            uploading: false,
            uploadProgress: 100,
            fileUrl: url,
            fileName: f.name,
            fileSize: f.size,
            fileType: f.type,
          }));
        });
      }
    );
  };

  const errorMessageCred = (err) => {
    const code = err?.response?.data?.error;
    switch (code) {
      case 'missing-certification': return 'Please pick a certification from the list.';
      case 'missing-org-or-title': return 'Please fill in the issuing organisation and credential name.';
      case 'missing-file': return 'Please upload a copy of your credential.';
      case 'missing-attestation': return 'Please confirm the attestation checkbox before submitting.';
      case 'invalid-year': return 'Please enter a valid year.';
      case 'limit-reached': return 'You\'ve reached the maximum number of credentials.';
      default: return 'Something went wrong. Please try again.';
    }
  };

  const handleCredSubmit = (e) => {
    e && e.preventDefault();
    setCredSubmitError(null);
    setCredSuccess(null);

    if (!credForm.certificationId) {
      setCredSubmitError('Please pick a certification from the dropdown.');
      return;
    }
    if (isOther && (!credForm.issuingOrgOther.trim() || !credForm.titleOther.trim())) {
      setCredSubmitError('For "Other", fill in both the issuing organisation and the credential name.');
      return;
    }
    if (!credForm.fileUrl) {
      setCredSubmitError('Please upload a copy of your credential.');
      return;
    }
    if (!credForm.attested) {
      setCredSubmitError('Please confirm the attestation.');
      return;
    }

    const payload = {
      certificationId: credForm.certificationId,
      issuingOrg: isOther ? credForm.issuingOrgOther.trim() : selectedCert.issuingOrg,
      title: isOther ? credForm.titleOther.trim() : selectedCert.title,
      issuedYear: credForm.issuedYear ? Number(credForm.issuedYear) : null,
      fileUrl: credForm.fileUrl,
      fileName: credForm.fileName,
      fileSize: credForm.fileSize,
      fileType: credForm.fileType,
      attested: true,
    };

    setCredSubmitting(true);
    onSubmitCred(payload)
      .then((res) => {
        const status = (res && res.data && res.data.status) || 'approved';
        setCredSubmitting(false);
        setCredSuccess(
          status === 'approved'
            ? 'Credential added and verified. It now counts toward your Verified badge.'
            : 'Credential submitted. Custom entries take 2-7 days to review.'
        );
        setCredForm({
          certificationId: '', issuingOrgOther: '', titleOther: '', issuedYear: '',
          file: null, fileError: null, uploadProgress: 0, uploading: false,
          fileUrl: '', fileName: '', fileSize: 0, fileType: '', attested: false,
        });
        onFetchCreds().catch(() => {});
      })
      .catch((err) => {
        setCredSubmitting(false);
        setCredSubmitError(errorMessageCred(err));
      });
  };

  const handleCredDelete = (id) => {
    if (!window.confirm('Remove this credential? If it was approved, your verified count will go down.')) return;
    setDeletingCredId(id);
    onDeleteCred(id)
      .then(() => {
        setDeletingCredId(null);
        onFetchCreds().catch(() => {});
      })
      .catch(() => setDeletingCredId(null));
  };

  const confirmed = (testimonials || []).filter((t) => t.status === 'confirmed').length;
  const pending = (testimonials || []).filter((t) => t.status === 'pending').length;
  const remaining = Math.max(0, MIN_VERIFIED_TESTIMONIALS - confirmed);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = (e) => {
    e && e.preventDefault();
    setSubmitError(null);
    setShowSuccess(false);

    // Client-side guard rails (backend re-validates, this is just UX)
    if (form.refereeName.trim().length < 2) {
      setSubmitError('Referee name is too short.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.refereeEmail.trim().toLowerCase())) {
      setSubmitError('Please enter a valid referee email.');
      return;
    }
    if (form.body.trim().length < 30) {
      setSubmitError('Testimonial body must be at least 30 characters.');
      return;
    }

    setSubmitting(true);
    onSubmit(form)
      .then(() => {
        setSubmitting(false);
        setShowSuccess(true);
        setForm({
          refereeName: '',
          refereeEmail: '',
          refereeOrg: '',
          refereeTitle: '',
          refereeLinkedInUrl: '',
          body: '',
          programOrEngagement: '',
        });
        onFetch().catch(() => {});
      })
      .catch((err) => {
        setSubmitting(false);
        setSubmitError(errorMessage(err));
      });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this testimonial? If it was confirmed, your verified count will go down.')) {
      return;
    }
    setDeletingId(id);
    onDelete(id)
      .then(() => {
        setDeletingId(null);
        onFetch().catch(() => {});
      })
      .catch(() => setDeletingId(null));
  };

  // Wave 1 plan-tier gate — Verification (testimonials + credentials)
  // is a Verified-tier feature. Network coaches see the inline
  // UpgradeGate instead of the editor. Note: this is the PLAN tier
  // gate, separate from the public BADGE eligibility (which requires
  // confirmed testimonials + approved certifications regardless of
  // plan tier).
  const canAccessVerification = meetsTierGate(currentUser, TIERS.VERIFIED);
  if (!canAccessVerification) {
    return (
      <PageWrapper>
        <MainDashboardWrapper title="Verification" page="coach-testimonials">
          <SettingsLayoutWrapper page="verification">
            <UpgradeGate
              featureName="Verification"
              requiredTier={TIERS.VERIFIED}
              currentUser={currentUser}
              benefits={[
                'Collect confirmed testimonials from your clients',
                'Upload professional credentials for admin approval',
                'Earn the public Verified badge on your listing',
                'Show trust signals to prospective clients',
              ]}
            />
          </SettingsLayoutWrapper>
        </MainDashboardWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <MainDashboardWrapper title="Verification" page="coach-testimonials">
        <SettingsLayoutWrapper page="verification">
        <div className={css.base}>
          {/* ── Verification status strip (two gates) ─────────────
              Public Verified badge requires BOTH gates to pass:
                • >= MIN_VERIFIED_TESTIMONIALS confirmed testimonials
                • >= MIN_VERIFIED_CERTIFICATIONS approved certifications
              We show progress on each independently so the coach
              knows exactly what's missing. */}
          {(() => {
            const vs = getVerificationStatus(currentUser);
            const both = vs.badgeUnlocked;
            return (
              <div className={`${css.statusStrip} ${both ? css.statusStrip_ok : ''}`}>
                <div className={css.statusStripHeader}>
                  <span className={css.statusStripTitle}>
                    {both ? "You've unlocked the Verified badge" : 'Unlock your Verified badge'}
                  </span>
                  <span className={css.statusStripHint}>
                    {both
                      ? `You've met the minimum of ${vs.testimonials.required} confirmed testimonials and ${vs.certifications.required} approved certification${vs.certifications.required === 1 ? '' : 's'}.`
                      : `Get at least ${vs.testimonials.required} confirmed testimonials and ${vs.certifications.required} approved certification${vs.certifications.required === 1 ? '' : 's'} to unlock the badge.`}
                  </span>
                </div>
                <div className={css.statusStripGates}>
                  <div className={`${css.statusGate} ${vs.testimonials.passed ? css.statusGate_ok : ''}`}>
                    <div className={css.statusGateIcon}>{vs.testimonials.passed ? '✓' : vs.testimonials.confirmed}</div>
                    <div className={css.statusGateText}>
                      <div className={css.statusGateLabel}>Client testimonials</div>
                      <div className={css.statusGateValue}>
                        {vs.testimonials.confirmed} of {vs.testimonials.required} confirmed
                      </div>
                    </div>
                  </div>
                  <div className={`${css.statusGate} ${vs.certifications.passed ? css.statusGate_ok : ''}`}>
                    <div className={css.statusGateIcon}>{vs.certifications.passed ? '✓' : vs.certifications.approved}</div>
                    <div className={css.statusGateText}>
                      <div className={css.statusGateLabel}>Professional certifications</div>
                      <div className={css.statusGateValue}>
                        {vs.certifications.approved} of {vs.certifications.required} approved
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Submission form ── */}
          <div className={css.formCard}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>
                <span className={css.cardHeaderIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                </span>
                Request a testimonial
              </h2>
            </div>
            <div className={css.cardBody}>
            <p className={css.cardSubtitle}>
              Enter the testimonial you'd like your client to confirm. We'll email them a unique link to verify it.
            </p>

            <form onSubmit={handleSubmit}>
              <div className={css.row}>
                <div className={css.col}>
                  <label className={css.fieldLabel}>Referee name <span className={css.req}>*</span></label>
                  <input
                    type="text"
                    className={css.input}
                    value={form.refereeName}
                    onChange={(e) => setField('refereeName', e.target.value)}
                    placeholder="Sarah Patel"
                  />
                </div>
                <div className={css.col}>
                  <label className={css.fieldLabel}>Referee email <span className={css.req}>*</span></label>
                  <input
                    type="email"
                    className={css.input}
                    value={form.refereeEmail}
                    onChange={(e) => setField('refereeEmail', e.target.value)}
                    placeholder="sarah@example.com"
                  />
                </div>
              </div>

              <div className={css.row}>
                <div className={css.col}>
                  <label className={css.fieldLabel}>Organisation</label>
                  <input
                    type="text"
                    className={css.input}
                    value={form.refereeOrg}
                    onChange={(e) => setField('refereeOrg', e.target.value)}
                    placeholder="Acme Inc."
                  />
                </div>
                <div className={css.col}>
                  <label className={css.fieldLabel}>Their job title</label>
                  <input
                    type="text"
                    className={css.input}
                    value={form.refereeTitle}
                    onChange={(e) => setField('refereeTitle', e.target.value)}
                    placeholder="VP of Engineering"
                  />
                </div>
              </div>

              <label className={css.fieldLabel}>LinkedIn URL (recommended)</label>
              <input
                type="url"
                className={css.input}
                value={form.refereeLinkedInUrl}
                onChange={(e) => setField('refereeLinkedInUrl', e.target.value)}
                placeholder="https://www.linkedin.com/in/sarahpatel"
              />

              <label className={css.fieldLabel}>What program / context (optional)</label>
              <input
                type="text"
                className={css.input}
                value={form.programOrEngagement}
                onChange={(e) => setField('programOrEngagement', e.target.value)}
                placeholder="6-month leadership program"
              />

              <label className={css.fieldLabel}>The testimonial <span className={css.req}>*</span></label>
              <textarea
                className={css.input}
                value={form.body}
                onChange={(e) => setField('body', e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Working with Erin transformed how I lead my team..."
                style={{ resize: 'vertical', minHeight: 120, fontFamily: 'inherit' }}
              />
              <span className={css.charCount}>{form.body.length} / 2000</span>

              {submitError && <div className={css.errorBanner}>{submitError}</div>}
              {showSuccess && (
                <div className={css.successBanner}>
                  Email sent. {form.refereeName || 'The referee'} has 14 days to confirm.
                </div>
              )}

              <div className={css.actions}>
                <button
                  type="submit"
                  className={css.primaryButton}
                  disabled={submitting}
                >
                  {submitting ? 'Sending…' : 'Send confirmation email'}
                </button>
              </div>
            </form>
            </div>
          </div>

          {/* ── Credentials section (file upload + list) ── */}
          <div className={css.formCard}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>
                <span className={css.cardHeaderIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="8" r="6"/>
                    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
                  </svg>
                </span>
                Add a certification
              </h2>
            </div>
            <div className={css.cardBody}>
            <p className={css.cardSubtitle}>
              Upload your coaching certification. Standard credentials from the list below appear instantly.
              Custom entries (Other) take 2-7 days for a Viveka review before they count toward your badge.
            </p>

            <form onSubmit={handleCredSubmit}>
              <label className={css.fieldLabel}>Certification <span className={css.req}>*</span></label>
              <select
                className={css.input}
                value={credForm.certificationId}
                onChange={(e) => setCredForm({ ...credForm, certificationId: e.target.value })}
              >
                <option value="">— Choose your certification —</option>
                {CERTS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id === 'other' ? c.title : `${c.issuingOrg} — ${c.title}`}
                  </option>
                ))}
              </select>

              {isOther && (
                <>
                  <div className={css.row}>
                    <div className={css.col}>
                      <label className={css.fieldLabel}>Issuing organisation <span className={css.req}>*</span></label>
                      <input
                        type="text"
                        className={css.input}
                        value={credForm.issuingOrgOther}
                        onChange={(e) => setCredForm({ ...credForm, issuingOrgOther: e.target.value })}
                        placeholder="e.g. The Coaches Training Institute"
                      />
                    </div>
                    <div className={css.col}>
                      <label className={css.fieldLabel}>Credential name <span className={css.req}>*</span></label>
                      <input
                        type="text"
                        className={css.input}
                        value={credForm.titleOther}
                        onChange={(e) => setCredForm({ ...credForm, titleOther: e.target.value })}
                        placeholder="e.g. Advanced Coaching Diploma"
                      />
                    </div>
                  </div>
                  <p className={css.hint}>
                    Custom credentials are placed in a review queue. Once a Viveka team member confirms it
                    (typically 2-7 days), the credential becomes visible and counts toward your Verified badge.
                  </p>
                </>
              )}

              <label className={css.fieldLabel}>Year issued (optional)</label>
              {/* Dropdown years instead of <input type=number> — that
                  control has annoying spinner arrows, accepts mouse
                  scroll changes (easy to fat-finger), and forces the
                  coach to type a four-digit number. The dropdown is
                  sorted newest-first so the common picks are at the top. */}
              <select
                className={css.input}
                value={credForm.issuedYear}
                onChange={(e) => setCredForm({ ...credForm, issuedYear: e.target.value })}
                style={{ maxWidth: 220 }}
              >
                <option value="">— Choose a year —</option>
                {Array.from({ length: 57 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <label className={css.fieldLabel}>
                Upload credential file <span className={css.req}>*</span>
              </label>
              <p className={css.hint}>PDF, JPG, or PNG &mdash; up to 10 MB.</p>
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={handleFileSelect}
                disabled={credForm.uploading}
              />
              {credForm.uploading && (
                <p className={css.hint}>Uploading&hellip; {credForm.uploadProgress}%</p>
              )}
              {credForm.fileUrl && !credForm.uploading && (
                <p className={css.successInline}>
                  &#10003; {credForm.fileName} uploaded
                </p>
              )}
              {credForm.fileError && (
                <p className={css.errorBanner}>{credForm.fileError}</p>
              )}

              <div className={css.attestation}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={credForm.attested}
                    onChange={(e) => setCredForm({ ...credForm, attested: e.target.checked })}
                    style={{ marginTop: '3px' }}
                  />
                  <span style={{ fontSize: '13px', lineHeight: '1.5', color: '#475569' }}>
                    I confirm this credential is genuine and I'm authorised to display it.
                    Misrepresentation may result in account suspension.
                  </span>
                </label>
              </div>

              {credSubmitError && <div className={css.errorBanner}>{credSubmitError}</div>}
              {credSuccess && <div className={css.successBanner}>{credSuccess}</div>}

              <div className={css.actions}>
                <button
                  type="submit"
                  className={css.primaryButton}
                  disabled={credSubmitting || credForm.uploading || !credForm.fileUrl}
                >
                  {credSubmitting ? 'Submitting&hellip;' : 'Add credential'}
                </button>
              </div>
            </form>
            </div>
          </div>

          <div className={css.listCard}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>
                <span className={css.cardHeaderIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 11 12 14 22 4"/>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                </span>
                Your certifications
              </h2>
            </div>
            <div className={css.cardBody}>
            {credentialsFetching && <IconSpinner />}
            {!credentialsFetching && (!credentials || credentials.length === 0) && (
              <p className={css.empty}>You haven't added any certifications yet.</p>
            )}
            {!credentialsFetching && credentials && credentials.length > 0 && (
              <ul className={css.list}>
                {credentials.map((c) => {
                  const statusTone = c.status === 'approved' ? 'confirmed'
                    : c.status === 'rejected' ? 'rejected'
                    : 'pending';
                  const statusText = c.status === 'approved' ? 'Approved'
                    : c.status === 'rejected' ? 'Rejected'
                    : 'Pending review';
                  return (
                    <li key={c.id} className={css.listRow}>
                      <div className={css.listRowHead}>
                        <div>
                          <strong className={css.refName}>{c.title}</strong>
                          <span className={css.refMeta}>
                            {c.issuingOrg ? ` · ${c.issuingOrg}` : ''}
                            {c.issuedYear ? ` · ${c.issuedYear}` : ''}
                          </span>
                        </div>
                        <span className={`${css.statusPill} ${css['statusPill_' + statusTone]}`}>
                          {statusText}
                        </span>
                      </div>
                      {c.rejectionReason && (
                        <p className={css.body} style={{ color: '#B91C1C' }}>
                          Reason: {c.rejectionReason}
                        </p>
                      )}
                      <div className={css.listRowFoot}>
                        <span className={css.dateMeta}>
                          {c.fileUrl && (
                            <a href={c.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#64748B', textDecoration: 'underline' }}>
                              View file ({c.fileName})
                            </a>
                          )}
                        </span>
                        <button
                          type="button"
                          className={css.deleteButton}
                          disabled={deletingCredId === c.id}
                          onClick={() => handleCredDelete(c.id)}
                        >
                          {deletingCredId === c.id ? 'Deleting&hellip;' : 'Delete'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            </div>
          </div>

          {/* ── Existing testimonials list ── */}
          <div className={css.listCard}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>
                <span className={css.cardHeaderIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                    <path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                </span>
                Your testimonials
              </h2>
            </div>
            <div className={css.cardBody}>
            {testimonialsFetching && <IconSpinner />}

            {!testimonialsFetching && (!testimonials || testimonials.length === 0) && (
              <p className={css.empty}>You haven't requested any testimonials yet.</p>
            )}

            {!testimonialsFetching && testimonials && testimonials.length > 0 && (
              <ul className={css.list}>
                {testimonials.map((t) => {
                  const status = STATUS_LABEL[t.status] || { text: t.status, tone: 'pending' };
                  return (
                    <li key={t.id} className={css.listRow}>
                      <div className={css.listRowHead}>
                        <div>
                          <strong className={css.refName}>{t.refereeName}</strong>
                          <span className={css.refMeta}>
                            {t.refereeOrg && ` · ${t.refereeOrg}`}
                            {` · ${t.refereeEmail}`}
                          </span>
                        </div>
                        <span className={`${css.statusPill} ${css['statusPill_' + status.tone]}`}>
                          {status.text}
                        </span>
                      </div>
                      <p className={css.body}>"{t.body}"</p>
                      <div className={css.listRowFoot}>
                        <span className={css.dateMeta}>
                          Submitted {formatDate(t.submittedAt)}
                          {t.confirmedAt && ` · Confirmed ${formatDate(t.confirmedAt)}`}
                        </span>
                        <button
                          type="button"
                          className={css.deleteButton}
                          disabled={deletingId === t.id}
                          onClick={() => handleDelete(t.id)}
                        >
                          {deletingId === t.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            </div>
          </div>
        </div>
        </SettingsLayoutWrapper>
      </MainDashboardWrapper>
    </PageWrapper>
  );
}

const mapStateToProps = (state) => {
  const { testimonials, testimonialsFetching } = state.testimonials || {};
  const { credentials, credentialsFetching } = state.credentials || {};
  const { currentUser } = state.user || {};
  return {
    testimonials: testimonials || [],
    testimonialsFetching: !!testimonialsFetching,
    credentials: credentials || [],
    credentialsFetching: !!credentialsFetching,
    currentUser,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onFetch: () => dispatch(fetchMyTestimonials()),
  onSubmit: (payload) => dispatch(submitTestimonial(payload)),
  onDelete: (id) => dispatch(deleteTestimonial(id)),
  onFetchCreds: () => dispatch(fetchMyCredentials()),
  onSubmitCred: (payload) => dispatch(submitCredential(payload)),
  onDeleteCred: (id) => dispatch(deleteCredential(id)),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(SaaSCoachTestimonialsPage);
